import { motion } from "framer-motion";
import { LightArt } from "./LightArt";

const chapters = [
  {
    id: "wer",
    kicker: "I.",
    heading: "Wer die Lichtmalerin ist",
    body: "Ich bin Fotografin — habe Fotodesign studiert — aber vor allem bin ich eine tief spirituelle Seele. Ein Mensch, der das Leben in all seinen Facetten spürt und nach schweren persönlichen Prüfungen und tiefen Tälern gelernt hat, wie unendlich kostbar die hellen Momente sind. Ich betrachte die Welt nicht nur durch eine Linse, sondern mit einem liebenden, gläubigen Herzen.",
    variant: 2,
  },
  {
    id: "berufung",
    kicker: "II.",
    heading: "Wie ich zur Lichtmalerei gekommen bin",
    body: "Es war kein Zufall, sondern eine Berufung. Die Fotografie war das Werkzeug, das mir geschenkt wurde — meine Spiritualität hat ihm eine Seele gegeben. Ich habe gespürt, dass ich die Natur nicht einfach nur abbilden will. Ich wollte das Göttliche, das in ihr wohnt, für andere sichtbar machen. So wurde aus der Fotografin die Lichtmalerin.",
    variant: 3,
  },
  {
    id: "daemmerung",
    kicker: "III.",
    heading: "Warum ich nachts und in der Dämmerung arbeite",
    body: "Weil ich weiß, dass das Licht seine schönste Kraft zeigt, wenn es die Dunkelheit durchbricht. Die Dämmerung ist die Zeit der Stille, in der die Welt den Atem anhält. Genau dort, im Übergang vom Schatten zum Schein, suche und finde ich die Nähe Gottes. Ich fange die Gewissheit ein, dass hinter jeder noch so tiefen Dunkelheit das Licht auf uns wartet.",
    variant: 5,
  },
  {
    id: "faszination",
    kicker: "IV.",
    heading: "Was mich am Licht fasziniert",
    body: "Für mich ist Licht kein physikalisches Phänomen, sondern die reinste Form von Hoffnung, Trost und göttlicher Energie. Es hat die Kraft zu heilen, den Nebel der Seele zu vertreiben und uns daran zu erinnern, dass wir niemals ganz im Finsteren gelassen werden.",
    variant: 6,
  },
];

export const About = () => {
  return (
    <section
      id="ueber-mich"
      data-testid="about-section"
      className="relative py-28 md:py-40 px-6 lg:px-12"
    >
      {/* --- Preamble / greeting --- */}
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1.2 }}
          viewport={{ once: true }}
          className="gold-divider"
          data-testid="about-eyebrow"
        >
          Ein Gruß
        </motion.span>

        <motion.p
          data-testid="about-intro"
          initial={{ opacity: 0, filter: "blur(20px)" }}
          whileInView={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          viewport={{ once: true, margin: "-80px" }}
          className="font-serif italic font-light text-2xl sm:text-3xl md:text-4xl leading-[1.35] tracking-tight text-[#E5E5E5] mt-10 max-w-3xl mx-auto"
        >
          „Schon so lange begleiten wir einander nun, und du weißt, dass in
          jedem Schatten ein Funken der{" "}
          <span className="gold-text not-italic">Hoffnung</span> wohnt. Als
          Lichtmalerin folge ich diesem Licht, das aus der{" "}
          <span className="gold-text not-italic">Schöpfung</span> kommt und
          durch die Dunkelheit strahlt. Jedes Bild ist eine Einladung, die
          Welt mit den{" "}
          <span className="gold-text not-italic">Augen des Glaubens</span> zu
          sehen und die heilende Kraft des Lichts zu spüren.“
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.4, delay: 0.6 }}
          viewport={{ once: true }}
          className="mx-auto mt-14 h-px w-40 origin-center"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.7), transparent)",
          }}
        />
      </div>

      {/* --- Section title --- */}
      <div className="max-w-6xl mx-auto text-center mt-24 md:mt-32 mb-16 md:mb-24 relative z-10">
        <motion.h2
          data-testid="about-heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          viewport={{ once: true }}
          className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#E5E5E5]"
        >
          Wer ist die <span className="italic gold-text">Lichtmalerin?</span>
        </motion.h2>
      </div>

      {/* --- Chapters --- */}
      <div className="max-w-7xl mx-auto flex flex-col gap-24 md:gap-32 relative z-10">
        {chapters.map((c, idx) => {
          const reverse = idx % 2 === 1;
          return (
            <div
              key={c.id}
              data-testid={`about-chapter-${c.id}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                reverse ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              {/* Text side */}
              <motion.div
                className="lg:col-span-7"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                viewport={{ once: true, margin: "-80px" }}
              >
                <p className="font-serif italic text-[#D4AF37]/70 text-xl mb-3">
                  {c.kicker}
                </p>
                <h3
                  data-testid={`about-chapter-heading-${c.id}`}
                  className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#E5E5E5] mb-6"
                >
                  {c.heading}
                </h3>
                <p
                  data-testid={`about-chapter-body-${c.id}`}
                  className="font-sans font-light text-base md:text-lg text-[#E5E5E5]/80 leading-relaxed max-w-xl"
                >
                  {c.body}
                </p>
              </motion.div>

              {/* Visual side */}
              <motion.div
                className="lg:col-span-5"
                initial={{ opacity: 0, filter: "blur(20px) brightness(0.15)" }}
                whileInView={{
                  opacity: 1,
                  filter: "blur(0px) brightness(1)",
                }}
                transition={{ duration: 1.8, ease: "easeOut" }}
                viewport={{ once: true, margin: "-80px" }}
              >
                <div
                  className="relative aspect-[4/5] overflow-hidden border border-[#D4AF37]/15"
                  style={{
                    boxShadow:
                      "0 0 50px rgba(212,175,55,0.10), inset 0 0 40px rgba(0,0,0,0.6)",
                  }}
                >
                  <LightArt variant={c.variant} />
                </div>
              </motion.div>
            </div>
          );
        })}
      </div>

      {/* --- Closing whisper --- */}
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.6 }}
        viewport={{ once: true }}
        className="mt-28 md:mt-40 text-center font-serif italic text-xl md:text-2xl text-[#D4AF37]/80 max-w-2xl mx-auto leading-snug"
        data-testid="about-outro"
      >
        „Licht ist meine Tinte. Zeit ist mein Papier.“
      </motion.p>
    </section>
  );
};
